/**
 * PERQ Floor Plan Match Block
 *
 * Injection block that loads the Fatwin / PERQ floor-plan matcher from the
 * Multihub field `custom_perq_script_url` (fatwin …/api/websites/resources/1?x=…).
 *
 * Fatwin paints a sitewide “Floor Plan Match” edge tab on home and interior
 * pages when that property’s PERQ account is configured for it. A script URL
 * being set is not the same as painting UI — many AION properties have
 * `custom_perq_script_url` and show nothing (Fairmont). That is correct.
 *
 * Hero CTA (“Find Your Perfect Floor Plan”) is a separate theme concern: a
 * button with class `fw-cta-button-target`. Fatwin binds `.fw-cta` /
 * `fw-cta-button-target`. Out of scope here — this block only injects the
 * script. It does not render a button or attach click handlers.
 *
 * Why singleton + never-tear-down: Fatwin paints the tab into document.body
 * (outside our tracking). Follow lea-chatbot / betterbot-chatbot: inject once
 * per tab, do not remove on unmount, or SPA remounts will duplicate the tab.
 */

import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';

const SCRIPT_ID = 'perq-floor-plan-script';

function PerqFloorPlanComponent(_props: BlockProps) {
  const scriptUrl = useBlockToken('script_url', {
    defaultField: 'custom_perq_script_url',
    defaultValue: '',
  })?.trim();

  useEffect(() => {
    if (!scriptUrl) return;
    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = scriptUrl;
    script.async = true;
    document.head.appendChild(script);
  }, [scriptUrl]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'PerqFloorPlan',
  displayName: 'PERQ Floor Plan Match',
  tokens: {
    script_url: {
      label: 'PERQ Script URL',
      type: 'text',
      defaultField: 'custom_perq_script_url',
      suggestedFields: ['custom_perq_script_url'],
      description:
        'Fatwin script URL from Multihub custom_perq_script_url (…/api/websites/resources/1?x=…). Empty is a no-op. Fatwin paints the Floor Plan Match tab only when PERQ enables it for the property.',
      required: true,
    },
  },
};

export const perqFloorPlanBlock: BlockDefinition = {
  id: 'perq-floor-plan',
  name: 'PERQ Floor Plan Match',
  description:
    'Loads the Fatwin / PERQ floor-plan matcher. Paints a sitewide “Floor Plan Match” tab when PERQ enables it for the property. A script URL being set does not mean UI will appear. Hero CTA (fw-cta-button-target) is a theme concern, not this block.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'global',
  component: PerqFloorPlanComponent,
  contentSchema,
  tags: ['perq', 'fatwin', 'floor plan', 'floorplan', 'matcher'],
};
